import React, { useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import AuthService from "src/services/authService";
import { updateStripeCard } from "src/ui/containers/account/api";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { Modal } from "src/ui/shared/Modal/Modal";
import { makeStyles } from "tss-react/mui";

import { Box, useTheme } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { ControlledCheckboxField } from "@shared/form/CheckboxField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";

type FormValues = { consent: boolean };

interface Props {
    open: boolean;
    onClose: () => void;
}

const useStyles = makeStyles()((theme: Theme) => ({
    formContent: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        paddingTop: theme.spacing(1),
    },
    cardElementWrapper: {
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper,
    },
}));

const EditStripeCard = (props: Props) => {
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const { classes } = useStyles();

    const stripe = useStripe();
    const elements = useElements();
    const theme = useTheme();
    const { t } = useTranslation(["pages", "common", "modals"]);

    const doSubmit = async (paymentMethodId: string | undefined) => {
        if (!paymentMethodId) {
            showErrorToast(t("pages:settingsPage.toast.updateStripeCardError"));
            setLoading(false);
            return;
        }

        try {
            const { message } = await updateStripeCard(paymentMethodId);
            showSuccessToast(message);
            setSuccess(true);
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        } finally {
            setLoading(false);
        }
    };
    const onSubmit = async (_values: FormValues) => {
        const cardElement = elements?.getElement(CardElement);

        if (!stripe || !elements || !cardElement) {
            return;
        }

        setLoading(true);

        const result = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
                email: AuthService.getCurrentUser()?.email,
            },
        });

        if (result.error) {
            setLoading(false);
            showErrorToast(result.error.message);
        } else {
            await doSubmit(result.paymentMethod?.id);
        }
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            initialValues={{ consent: false }}
            render={({ handleSubmit, form, values }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        title={t("modals:changeDefaultCard.title")}
                        open={props.open}
                        onClose={props.onClose}
                        endButton={
                            <PrimaryButton
                                onClick={form.submit}
                                disabled={loading || success || !values?.consent}
                                loading={loading && !success}
                                fullWidth
                            >
                                {!loading && success ? <i className="fa-regular fa-circle-check" /> : t("common:button.update")}
                            </PrimaryButton>
                        }
                    >
                        <Box className={classes.formContent}>
                            <ControlledCheckboxField
                                name="consent"
                                label={t("modals:changeDefaultCard.description")}
                                disabled={loading}
                                disableRipple
                            />
                            <Box className={classes.cardElementWrapper}>
                                <CardElement
                                    options={{
                                        style: {
                                            base: {
                                                fontSize: "16px",
                                                color: theme.palette.text.primary,
                                                "::placeholder": { color: theme.palette.text.secondary },
                                            },
                                            invalid: { color: theme.palette.error.main },
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    </Modal>
                </form>
            )}
        />
    );
};

export default EditStripeCard;
